import type { ComponentChildren } from "preact"

/**
 * The glyphs the surfaces use, as lucide draws them (lucide-react 1.31,
 * ISC): the exact path data, the same default attributes, `currentColor`
 * so a glyph takes its button's colour. Copied rather than depended on -
 * three icons are a few hundred bytes, the package is not.
 */

function Icon({
  size,
  children,
}: {
  size: number
  children: ComponentChildren
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

export const Sparkles = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
    <path d="M20 2v4" />
    <path d="M22 4h-4" />
    <circle cx="4" cy="20" r="2" />
  </Icon>
)

export const ArrowRight = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </Icon>
)

export const X = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Icon>
)

/** The visitor's "stop the conversation" control - lucide `MessageCircleX`. */
export const MessageCircleX = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
    <path d="m15 9-6 6" />
    <path d="m9 9 6 6" />
  </Icon>
)

// -- the Messenger preset's chrome (the preview draws the same lucide set) --

export const Phone = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
  </Icon>
)

export const Video = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
    <rect x="2" y="6" width="14" height="12" rx="2" />
  </Icon>
)

export const Info = ({ size }: { size: number }) => (
  <Icon size={size}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </Icon>
)

export const ChevronDown = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
)

export const Minus = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M5 12h14" />
  </Icon>
)

export const Send = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
    <path d="m21.854 2.147-10.94 10.939" />
  </Icon>
)

export const ExternalLink = ({ size }: { size: number }) => (
  <Icon size={size}>
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </Icon>
)

/** The Messenger mark on the launcher's badge - a fill glyph, not a stroke. */
export const MessengerLogo = ({ size, d }: { size: number; d: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
)
